import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * Horizontal scroller for the AGP member matrix. Wraps the table in
 * `.bd-table-wrap--matrix` and, when the table is wider than the card, shows
 * a thin bar with ‹ › buttons and a hint - overlay scrollbars (macOS) give no
 * visual cue that there is more to the right. Also toggles `can-left` /
 * `can-right` so the pinned columns only cast a shadow over hidden content.
 */
export default function MatrixScroller({ children, hint = "Scroll sideways to see every member", step = 320 }) {
  const ref = useRef(null);
  const [can, setCan] = useState({ left: false, right: false });

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setCan((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    if (ro) {
      ro.observe(el);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
    }
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      if (ro) ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [update, children]);

  const scroll = (dir) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  const scrollable = can.left || can.right;

  return (
    <div className={`bd-matrix-scroller ${can.left ? "can-left" : ""} ${can.right ? "can-right" : ""}`}>
      {scrollable && (
        <div className="bd-matrix-scroller__bar">
          <span className="bd-muted">{hint}</span>
          <span className="bd-matrix-scroller__nav">
            <button type="button" onClick={() => scroll(-1)} disabled={!can.left} aria-label="Scroll left" title="Scroll left">
              ‹
            </button>
            <button type="button" onClick={() => scroll(1)} disabled={!can.right} aria-label="Scroll right" title="Scroll right">
              ›
            </button>
          </span>
        </div>
      )}
      <div ref={ref} className="bd-table-wrap bd-table-wrap--matrix">
        {children}
      </div>
    </div>
  );
}
